import { useEffect, useRef } from 'react';
import { useClearToolbarFilterState } from 'hooks/filterToolbar/useClearFilters';
import { useResetMapSelections } from 'hooks/projectMap/useResetMapState';
import { useDebounce } from 'hooks/useDebounced';
import { useResetAllToolbarFilters, useResetFilterValues } from 'hooks/useFilterToolbar';
import { useGroupData } from 'hooks/useGroupData';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import { useCurrentRegion } from 'state/currentRegion';
import {
  useDashboardGroupType,
  useUserMultiRegionSelection,
  useUserStateSelection,
} from 'state/dashboard';
import {
  useAppliedMapGroupId,
  useMapProperties,
  useSelectedPolygonBoundsOnMap,
  useSelectedPolygonId,
} from 'state/map';
import {
  useCoordinatesQuery,
  useSearchQuery,
  useZoomQuery,
  useAddressQuery,
  useCenterQuery,
  useGroupIdQuery,
  useMapFiltersQuery,
  useRegionQuery,
  useStateCodeQuery,
} from 'state/queryParams';
import { DEFAULT_MAP_ZOOM, GROUP_TYPE, ZOOM_THRESHOLD } from 'utils/constants';
import {
  isValidRegion,
  METROPOLITAN_CODE_TO_STATE_CODE,
  Region,
  State,
  STATE_CODE_TO_LABEL,
  STATE_DEFAULT_COORDINATES,
} from 'utils/constants/location';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';
import { getCenterOfPolygon, getPolygonFromBoundingBox } from 'utils/polygons';

/**
 * Manages map interactions with the city query param.
 */
export const useMapLocationFromUrl = () => {
  const { regionQuery, setRegionQuery } = useRegionQuery();
  const { stateCodeQuery, setStateCodeQuery } = useStateCodeQuery();
  const { setCurrentRegion } = useCurrentRegion();
  const { availableRegions, availableStates } = useAvailableCities();
  const [selectedState, setSelectedState] = useUserStateSelection();
  const [selectedRegions, setSelectedRegions] = useUserMultiRegionSelection();

  /*
   * Set the current region and state onLoad.
   * This effect should only run once.
   * Do not add dependencies to this use effect, or it will cause negative side
   * effects.
   */
  useEffect(() => {
    let regionsToSet: Region[] = [];
    let stateToSet: State | null = null;

    // Parse regionQuery if it exists
    const parsedRegions: Region[] = regionQuery
      ? (regionQuery
          .split(',')
          .map((r) => r.trim())
          .filter((r) => isValidRegion(r)) as Region[])
      : [];

    // Check if all regions belong to one state
    let regionsStateCode: State | null = null;
    if (parsedRegions.length > 0) {
      const stateCodes = parsedRegions
        .map((region) => METROPOLITAN_CODE_TO_STATE_CODE[region])
        .filter((code): code is string => !!code);

      // Check if all regions belong to the same state
      const uniqueStateCodes = new Set(stateCodes);
      if (uniqueStateCodes.size === 1) {
        regionsStateCode = stateCodes[0] as State;
      }
    }

    // If we have a valid stateCodeQuery
    const validStateCodeQuery =
      stateCodeQuery && STATE_CODE_TO_LABEL[stateCodeQuery as State]
        ? (stateCodeQuery as State)
        : null;

    // Determine what to set based on validation
    if (validStateCodeQuery) {
      // If we have a stateCodeQuery, use it as the source of truth
      stateToSet = validStateCodeQuery;

      // If regions are valid and match the stateCode, use them
      if (
        regionsStateCode &&
        regionsStateCode === validStateCodeQuery &&
        parsedRegions.length > 0
      ) {
        // Filter regions to only include those in the correct state
        regionsToSet = parsedRegions;
      } else {
        // Otherwise, get all regions for the state
        const regionsForState = availableStates
          ? availableStates[validStateCodeQuery]
          : null;

        regionsToSet = regionsForState
          ? (Object.keys(regionsForState.regions) as Region[])
          : [];
      }
    } else if (regionsStateCode && parsedRegions.length > 0) {
      // If we have valid regions that all belong to one state, use them
      stateToSet = regionsStateCode;
      regionsToSet = parsedRegions;
    } else if (availableRegions) {
      // Fallback to available regions
      if (Region.US_AUSTIN in availableRegions) {
        regionsToSet = [Region.US_AUSTIN];
      } else {
        regionsToSet = [Object.keys(availableRegions)[0] as Region];
      }
      // Set state based on the first region
      if (regionsToSet.length > 0) {
        const firstRegionState = METROPOLITAN_CODE_TO_STATE_CODE[regionsToSet[0]];
        if (firstRegionState) {
          stateToSet = firstRegionState as State;
        }
      }
    } else if (availableStates) {
      // Default to a state (priority: TX, then FL) and ALL its available regions
      // Try TX first, then FL
      if (
        availableStates[State.TX] &&
        Object.keys(availableStates[State.TX].regions).length > 0
      ) {
        stateToSet = State.TX;
        regionsToSet = Object.keys(availableStates[State.TX].regions) as Region[];
      } else if (
        availableStates[State.FL] &&
        Object.keys(availableStates[State.FL].regions).length > 0
      ) {
        stateToSet = State.FL;
        regionsToSet = Object.keys(availableStates[State.FL].regions) as Region[];
      } else {
        // Fallback to first available state
        const firstAvailableState = Object.keys(availableStates)[0];
        if (firstAvailableState) {
          stateToSet = firstAvailableState as State;
          regionsToSet = Object.keys(
            availableStates[firstAvailableState].regions,
          ) as Region[];
        }
      }
    }

    // Set the state and regions
    if (stateToSet) {
      setSelectedState(stateToSet);
    }

    if (regionsToSet.length > 0) {
      setCurrentRegion(regionsToSet[0]);
      setSelectedRegions(regionsToSet);
    }
  }, [
    availableRegions,
    availableStates,
    setSelectedState,
    setSelectedRegions,
    setCurrentRegion,
  ]);

  // keep regionQuery aligned with currentRegion
  useEffect(() => {
    if (!selectedRegions?.length) {
      return;
    }
    setRegionQuery(selectedRegions.join(','));
  }, [selectedRegions]);

  useEffect(() => {
    if (selectedState) {
      setStateCodeQuery(selectedState);
    } else {
      setStateCodeQuery(undefined);
    }
  }, [selectedState]);
};

/**
 * Handles the map once it finishes loading.
 *
 * Center on the lat,lng query paramater if present.
 * Else, use center query paramater.
 * Else, default to city center.
 */
export const useHandleMapCenterUpdate = () => {
  const { currentRegionCenter } = useCurrentRegion();
  const { updateMapCenter, updateMapZoom } = useMapProperties();
  const { groupIdQuery } = useGroupIdQuery();
  const [selectedState] = useUserStateSelection();
  // On the initial load, honor a shared link's center/zoom. Afterwards, only
  // recenter to the state default when `selectedState` actually changes (user
  // switching state) — NOT on incidental re-renders, which would otherwise
  // clobber the restored view or the user's current pan/zoom. These refs live
  // above the `selectedState`-keyed ClusterMap (see Map.tsx), so they survive
  // that child's remount.
  const hasInitializedRef = useRef(false);
  const prevSelectedStateRef = useRef(selectedState);

  useEffect(() => {
    if (groupIdQuery) {
      return;
    }

    const applyStateDefault = () => {
      const { lat, lng } = STATE_DEFAULT_COORDINATES[selectedState as State];
      updateMapCenter({ lat, lng });
      updateMapZoom(DEFAULT_MAP_ZOOM);
    };

    // First run: restore center/zoom from the URL if present; otherwise fall
    // back to the state default.
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      prevSelectedStateRef.current = selectedState;

      const params = new URLSearchParams(window.location.search);
      const centerQuery = params.get(QUERY_PARAM.center);
      const zoomQuery = params.get(QUERY_PARAM.zoom);

      if (centerQuery || zoomQuery) {
        if (centerQuery) {
          const [lat, lng] = centerQuery.split(',');
          updateMapCenter({ lat: parseFloat(lat), lng: parseFloat(lng) });
        } else {
          updateMapCenter(currentRegionCenter);
        }
        if (zoomQuery && !Number.isNaN(Number(zoomQuery))) {
          updateMapZoom(Number(zoomQuery));
        }

        return;
      }

      applyStateDefault();

      return;
    }

    // Subsequent runs: only recenter when the selected state changed. Ignore
    // incidental re-renders so a restored/panned view isn't reset.
    if (prevSelectedStateRef.current !== selectedState) {
      prevSelectedStateRef.current = selectedState;
      applyStateDefault();
    }
  }, [updateMapCenter, updateMapZoom, groupIdQuery, selectedState, currentRegionCenter]);
};

/**
 * Handle map changes when user selects a group on the toolbar OR a group is set via URL param.
 */
export const useHandleProjectGroupSelect = () => {
  const { groupIdQuery } = useGroupIdQuery();
  const group = useGroupData(groupIdQuery ? String(groupIdQuery) : undefined);
  const { resetFilterValuesForGroupChange } = useResetFilterValues();
  const [, setSelectedPolygonBoundsOnMap] = useSelectedPolygonBoundsOnMap();
  const [, setSelectedPolygon] = useSelectedPolygonId();
  const { updateMapCenter, updateMapZoom } = useMapProperties();
  const [selectedState] = useUserStateSelection();
  const [, setAppliedMapGroupId] = useAppliedMapGroupId();

  useEffect(() => {
    if (group?.id && groupIdQuery && String(group.id) === groupIdQuery) {
      resetFilterValuesForGroupChange(group);
      // The selected group's filters are now applied to the map filter
      // state, so map fetches gated on useAreGroupFiltersReady can run.
      setAppliedMapGroupId(groupIdQuery);
      if (group.polygon_bounds) {
        // Calculate center from bounds and set active polygon on map
        setSelectedPolygonBoundsOnMap(group.polygon_bounds);
        setSelectedPolygon(group.polygon_bounds);
        const polygon = getPolygonFromBoundingBox(group.polygon_bounds);
        const { lat, lng } = getCenterOfPolygon(polygon);
        if (lat && lng) {
          updateMapCenter({ lat, lng });
        }

        return;
      }

      // No stored bounds to derive a center from (e.g. an AI/scoutbot search,
      // which never has polygon_bounds) - prefer a shared link's center/zoom
      // over the generic state default so copying the current URL and
      // reopening it lands back on the same view.
      const params = new URLSearchParams(window.location.search);
      const centerParam = params.get(QUERY_PARAM.center);
      const zoomParam = params.get(QUERY_PARAM.zoom);

      if (centerParam) {
        const [lat, lng] = centerParam.split(',').map(Number);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          updateMapCenter({ lat, lng });
        }
        if (zoomParam && !Number.isNaN(Number(zoomParam))) {
          updateMapZoom(Number(zoomParam));
        }

        return;
      }

      const { lat, lng } = STATE_DEFAULT_COORDINATES[selectedState as State];
      updateMapCenter({ lat, lng });
      updateMapZoom(DEFAULT_MAP_ZOOM);
    }
  }, [group?.id, groupIdQuery, selectedState]);
};

/* Resets query params on for the project map.
 * This hook is used to reset the map state when changing filters.
 */
export const useProjectMapQueryParams = () => {
  const [, setZoomQuery] = useZoomQuery();
  const [, setSearch] = useSearchQuery();
  const [, setAddress] = useAddressQuery();
  const { setCoordinatesQuery } = useCoordinatesQuery();
  const { setCenterQuery } = useCenterQuery();

  const resetAllQueryParamsForProjectMap = () => {
    setZoomQuery('');
    setSearch('');
    setAddress('');
    setCoordinatesQuery('');
    setCenterQuery('');
  };

  const clearProjectMapCoordinatePin = () => {
    setCoordinatesQuery('');
  };

  return {
    resetAllQueryParamsForProjectMap,
    clearProjectMapCoordinatePin,
  };
};

/**
 * Reset filters on map load
 */
export const useResetFiltersOnMapLoad = () => {
  const { groupIdQuery } = useGroupIdQuery();
  const { resetAllToolbarFilters } = useResetAllToolbarFilters();
  const { mapFiltersQuery, setMapFiltersQuery } = useMapFiltersQuery();
  const [, setGroupType] = useDashboardGroupType();
  const { resetAllMapSelections } = useResetMapSelections();
  const { clearToolbarFilterState } = useClearToolbarFilterState();

  useEffect(() => {
    // A shared link carries its filters in the `f` param; don't wipe those.
    const hasFilters = !!new URLSearchParams(window.location.search).get(
      QUERY_PARAM.filters,
    );
    if (!mapFiltersQuery && !groupIdQuery && !hasFilters) {
      /**
       * We don't want to reset filters if they were set on the map.
       * We use a query param to denote this state.
       * When navigating from top nav, clear all filters including stage filters.
       */
      resetAllToolbarFilters();
      // Also clear the latestProjectStageFilterState atom which is separate from dashboard state
      clearToolbarFilterState();
      setGroupType(GROUP_TYPE.PROJECT);
    }
    setMapFiltersQuery('true');
    resetAllMapSelections();
  }, []);
};

/**
 * Provides a debounced setCenterQuery abstraction.
 * This reduces the amount of map renders.
 */
export const useDebouncedSetCenterQuery = (center: string) => {
  const { setCenterQuery } = useCenterQuery();
  const debouncedCenter = useDebounce(center, 800);

  useEffect(() => {
    // Ensure center is initialized to a value.
    // We don't want to clear center unnecessarily.
    if (debouncedCenter) {
      setCenterQuery(debouncedCenter);
    }
  }, [debouncedCenter]);
};

export const useHandleCoordinatesUpdate = () => {
  const { coordinatesQuery, coordinatesAsLatLng } = useCoordinatesQuery();
  const { updateMapCenter, updateMapZoom } = useMapProperties();

  useEffect(() => {
    if (coordinatesQuery) {
      updateMapCenter(coordinatesAsLatLng);
      updateMapZoom(ZOOM_THRESHOLD);
    }
  }, [coordinatesQuery, coordinatesAsLatLng]);
};
